
import React, { useState } from 'react';
// Fix: LOCATIONS is exported from constants, not types
import { Staff, UserRole } from '../types';
import { LOCATIONS } from '../constants';

interface PeopleViewProps {
  staff: Staff[];
  onUpdateStaff: React.Dispatch<React.SetStateAction<Staff[]>>;
}

const PeopleView: React.FC<PeopleViewProps> = ({ staff, onUpdateStaff }) => {
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStaff) return;
    
    onUpdateStaff(prev => prev.map(s => s.id === editingStaff.id ? editingStaff : s));
    setEditingStaff(null);
  };

  const toggleLocation = (locId: string) => {
    if (!editingStaff) return;
    const current = editingStaff.assignedLocationIds;
    const updated = current.includes(locId) 
      ? current.filter(id => id !== locId)
      : [...current, locId];
    setEditingStaff({ ...editingStaff, assignedLocationIds: updated });
  };

  return (
    <div className="space-y-8 md:space-y-10 pb-10 px-2 md:px-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b-2 md:border-b-4 border-black pb-6 md:pb-8 gap-4">
        <h2 className="ymca-brand-font text-5xl md:text-7xl tracking-tighter leading-none">PEOPLE & SKILLS</h2>
        <button className="w-full sm:w-auto px-6 md:px-10 py-3 md:py-4 bg-black text-white font-black text-[10px] md:text-xs tracking-widest uppercase hover:bg-gray-800 transition-all">
          ONBOARD NEW STAFF
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-black bg-black">
        {staff.map(member => (
          <div key={member.id} className="bg-white border-r border-b border-black last:border-r-0 p-6 md:p-10 group hover:bg-black transition-all duration-300">
            <div className="flex justify-between items-start mb-6 md:mb-10">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-black text-white group-hover:bg-white group-hover:text-black flex items-center justify-center text-3xl md:text-4xl ymca-brand-font transition-colors">
                {member.firstName[0]}
              </div>
              <div className={`px-3 md:px-4 py-1 border border-black group-hover:border-white text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all ${
                member.complianceStatus === 'Active' ? 'text-black group-hover:text-white' : 
                'bg-ymca-orange text-white border-none'
              }`}>
                {member.complianceStatus}
              </div>
            </div>

            <div className="space-y-1 mb-6 md:mb-8">
              <h4 className="ymca-brand-font text-4xl md:text-5xl tracking-tight leading-none group-hover:text-white transition-colors">{member.firstName} {member.lastName}</h4>
              <p className="text-[10px] md:text-xs font-bold text-gray-400 group-hover:text-gray-500 uppercase tracking-widest">{member.role.replace('_', ' ')}</p>
            </div>

            <div className="space-y-3 md:space-y-4 pt-6 md:pt-8 border-t border-black/10 group-hover:border-white/10">
              <div className="flex justify-between text-[8px] md:text-[10px] font-black tracking-widest uppercase text-gray-500 group-hover:text-gray-400">
                <span>Contract</span>
                <span className="text-black group-hover:text-white">{member.contractType}</span>
              </div>
              <div className="flex justify-between text-[8px] md:text-[10px] font-black tracking-widest uppercase text-gray-500 group-hover:text-gray-400">
                <span>Capacity</span>
                <span className="text-black group-hover:text-white">{member.maxWeeklyHours}H / WEEK</span>
              </div>
              <div className="flex justify-between text-[8px] md:text-[10px] font-black tracking-widest uppercase text-gray-500 group-hover:text-gray-400">
                <span>Allocations</span>
                <span className="text-black group-hover:text-white">{member.assignedLocationIds.length} SITES</span>
              </div>
            </div>

            <div className="mt-8 md:mt-10 lg:opacity-0 group-hover:opacity-100 transition-opacity">
              <button 
                onClick={() => setEditingStaff(member)}
                className="w-full py-3 md:py-4 border-2 border-black md:border-white text-black md:text-white font-black text-[10px] tracking-widest uppercase hover:bg-black hover:text-white md:hover:bg-white md:hover:text-black transition-all group-hover:border-white"
              >
                EDIT PROFILE
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* EDIT MODAL */}
      {editingStaff && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-4xl p-8 md:p-12 border-4 border-black relative my-auto animate-in zoom-in duration-200">
            <button 
              onClick={() => setEditingStaff(null)}
              className="absolute top-6 right-6 text-black hover:text-ymca-orange transition-colors text-4xl leading-none font-bold"
            >
              &times;
            </button>
            
            <form onSubmit={handleSave} className="space-y-10">
              <div className="space-y-2">
                <span className="text-ymca-orange font-bold text-[10px] uppercase tracking-[0.3em]">Workforce Management</span>
                <h2 className="ymca-brand-font text-5xl md:text-7xl leading-none uppercase text-black">Edit Staff Profile</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Left Column: Basic Details */}
                <div className="space-y-6">
                  <h3 className="ymca-brand-font text-3xl uppercase border-b-2 border-black pb-2">Personal & Role</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">First Name</label>
                      <input 
                        type="text" 
                        value={editingStaff.firstName}
                        onChange={e => setEditingStaff({...editingStaff, firstName: e.target.value})}
                        className="w-full border-2 border-black p-3 text-sm font-bold focus:border-ymca-orange outline-none"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Last Name</label>
                      <input 
                        type="text" 
                        value={editingStaff.lastName}
                        onChange={e => setEditingStaff({...editingStaff, lastName: e.target.value})}
                        className="w-full border-2 border-black p-3 text-sm font-bold focus:border-ymca-orange outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Job Role</label>
                    <select 
                      value={editingStaff.role}
                      onChange={e => setEditingStaff({...editingStaff, role: e.target.value as UserRole})}
                      className="w-full border-2 border-black p-3 text-sm font-bold focus:border-ymca-orange outline-none appearance-none bg-white"
                    >
                      {Object.values(UserRole).map(role => (
                        <option key={role} value={role}>{role.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Contract</label>
                      <select 
                        value={editingStaff.contractType}
                        onChange={e => setEditingStaff({...editingStaff, contractType: e.target.value as any})}
                        className="w-full border-2 border-black p-3 text-sm font-bold focus:border-ymca-orange outline-none appearance-none bg-white"
                      >
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Zero-hours">Zero-hours</option>
                        <option value="Volunteer">Volunteer</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Max Weekly Hrs</label>
                      <input 
                        type="number" 
                        value={editingStaff.maxWeeklyHours}
                        onChange={e => setEditingStaff({...editingStaff, maxWeeklyHours: parseInt(e.target.value)})}
                        className="w-full border-2 border-black p-3 text-sm font-bold focus:border-ymca-orange outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column: Site Allocations */}
                <div className="space-y-6">
                  <h3 className="ymca-brand-font text-3xl uppercase border-b-2 border-black pb-2">Site Allocations</h3>
                  <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2 scrollbar-hide">
                    {LOCATIONS.map(loc => (
                      <div 
                        key={loc.id}
                        onClick={() => toggleLocation(loc.id)}
                        className={`p-3 border-2 cursor-pointer transition-all flex items-center justify-between ${
                          editingStaff.assignedLocationIds.includes(loc.id) 
                          ? 'border-black bg-black text-white' 
                          : 'border-gray-200 hover:border-black'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="ymca-brand-font text-xl leading-none truncate uppercase">{loc.name}</p>
                          <p className={`text-[8px] font-bold uppercase tracking-widest ${editingStaff.assignedLocationIds.includes(loc.id) ? 'text-gray-400' : 'text-gray-400'}`}>{loc.type}</p>
                        </div>
                        {editingStaff.assignedLocationIds.includes(loc.id) && (
                          <svg className="w-5 h-5 text-ymca-orange" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t-2 border-black flex flex-col md:flex-row gap-4">
                <button 
                  type="button"
                  onClick={() => setEditingStaff(null)}
                  className="flex-1 py-4 border-2 border-black font-black text-xs tracking-widest uppercase hover:bg-gray-100 transition-all text-black"
                >
                  Discard Changes
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-4 bg-black text-white font-black text-xs tracking-widest uppercase hover:bg-ymca-orange transition-all border-2 border-black"
                >
                  Save Profile Details
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeopleView;
